import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_VISIBLE_LOG_COUNT,
  MAX_PLAY_LOG_COUNT,
  NEAR_FAIL_DIFF_MS,
  PLAY_LOG_EXPIRE_MS,
  SUCCESS_DAILY_LIMIT,
  TARGET_MS,
  TIMER_MAX_MS,
} from '../constants';
import { formatSecondsText, formatTimer, getTodayKstKey, toDisplayMs } from '../utils/time';

const clampElapsedMs = (rawMs) => Math.min(TIMER_MAX_MS, Math.max(0, rawMs));

const toStopDisplayMs = (rawMs) => toDisplayMs(clampElapsedMs(rawMs));

const parseServerDateTimeToMs = (value) => {
  if (value === null || value === undefined) return 0;

  if (typeof value === 'number' && Number.isFinite(value)) {
    // Accept both epoch seconds and epoch milliseconds.
    return value < 1_000_000_000_000 ? Math.round(value * 1000) : Math.round(value);
  }

  if (typeof value !== 'string') return 0;
  const normalized = value.trim();
  if (!normalized) return 0;

  const directParsed = new Date(normalized.replace(' ', 'T')).getTime();
  if (!Number.isNaN(directParsed)) return directParsed;

  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/);
  if (!match) return 0;

  const [, y, m, d, hh, mm, ss, msRaw = '0'] = match;
  const ms = Number(msRaw.padEnd(3, '0'));
  return new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss), ms).getTime();
};

export default function useTimeNyangGame({
  getStartContext,
  onPlayStart,
  onPlayStop = null,
  onResult,
  isBlockedByPopup,
  initState = null,
}) {
  const [playState, setPlayState] = useState('idle');
  const [timerMs, setTimerMs] = useState(0);
  const [playLogs, setPlayLogs] = useState([]);
  const [isLogsExpanded, setIsLogsExpanded] = useState(false);
  const [bestLog, setBestLog] = useState(null);
  const [dailySuccessCount, setDailySuccessCount] = useState(0);
  const [isDailyLocked, setIsDailyLocked] = useState(false);
  const [isStartPending, setIsStartPending] = useState(false);
  const [isStopPending, setIsStopPending] = useState(false);
  const [todayKstKey, setTodayKstKey] = useState(getTodayKstKey);

  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  const beepIntervalRef = useRef(null);
  const startAtRef = useRef(0);
  const elapsedBeforeStartRef = useRef(0);
  /** 마지막 rAF에서 화면에 그린 경과 ms — STOP 시 performance.now() 재계산 대신 사용 */
  const lastDisplayedMsRef = useRef(0);

  const clearRaf = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const getAudioContext = useCallback(() => {
    if (!audioCtxRef.current) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return null;
      audioCtxRef.current = new AudioContext();
    }
    return audioCtxRef.current;
  }, []);

  const playBeep = useCallback(() => {
    try {
      const ctx = getAudioContext();
      if (!ctx) return;
      if (ctx.state === 'suspended') ctx.resume();

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = 800;
      gain.gain.value = 0.15;
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.08);
    } catch (error) {
      // AudioContext can be unavailable in some embedded browsers.
    }
  }, [getAudioContext]);

  const stopBeep = useCallback(() => {
    if (beepIntervalRef.current) {
      window.clearInterval(beepIntervalRef.current);
      beepIntervalRef.current = null;
    }
  }, []);

  const startBeep = useCallback(() => {
    stopBeep();
    playBeep();
    beepIntervalRef.current = window.setInterval(playBeep, 1000);
  }, [playBeep, stopBeep]);

  const pruneExpiredLogs = useCallback((logs, now = Date.now()) => {
    const filtered = logs.filter((log) => {
      const createdAtMs = Number(log?.createdAt);
      if (!Number.isFinite(createdAtMs) || createdAtMs <= 0) {
        // Keep unknown timestamps instead of dropping logs unexpectedly.
        return true;
      }
      return now - createdAtMs < PLAY_LOG_EXPIRE_MS;
    });
    return filtered.slice(0, MAX_PLAY_LOG_COUNT);
  }, []);

  const getBetterLog = useCallback((prevBestLog, nextLog) => {
    if (!prevBestLog) return nextLog;
    if (nextLog.absDiffSec < prevBestLog.absDiffSec) return nextLog;
    if (nextLog.absDiffSec === prevBestLog.absDiffSec && nextLog.createdAt > prevBestLog.createdAt) return nextLog;
    return prevBestLog;
  }, []);

  const createLog = useCallback((elapsedMs) => {
    const normalizedMs = toDisplayMs(elapsedMs);
    const diffSec = Number(((normalizedMs - TARGET_MS) / 1000).toFixed(2));
    const absDiffSec = Math.abs(diffSec);
    const isExact = absDiffSec === 0;
    const diffText = isExact ? '정확!' : `${diffSec > 0 ? '+' : ''}${diffSec.toFixed(2)}`;
    const tone = isExact ? 'text-[#03C75A]' : absDiffSec <= 0.1 ? 'text-[#FFBC2B]' : 'text-[#F6475F]';

    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      createdAt: Date.now(),
      elapsedMs: normalizedMs,
      timeText: formatSecondsText(normalizedMs),
      diffSec,
      absDiffSec,
      diffText,
      tone,
    };
  }, []);

  const createServerLog = useCallback((historyItem) => {
    const elapsedMs = Math.round(Number(historyItem.elapsedSec ?? 0) * 1000);
    const absDiffSec = Math.abs(Number(historyItem.diffSec ?? 0));
    const isExact = absDiffSec === 0 || historyItem.resultType === 'success';
    const diffText = isExact ? '정확!' : `+${absDiffSec.toFixed(2)}`;
    const tone = isExact ? 'text-[#03C75A]' : absDiffSec <= 0.1 ? 'text-[#FFBC2B]' : 'text-[#F6475F]';

    return {
      id: `server-${historyItem.historyNo ?? `${historyItem.createdAt}-${historyItem.elapsedSec}`}`,
      createdAt: parseServerDateTimeToMs(historyItem.createdAt) || Date.now(),
      elapsedMs,
      timeText: formatSecondsText(elapsedMs),
      diffSec: isExact ? 0 : absDiffSec,
      absDiffSec,
      diffText,
      tone,
    };
  }, []);

  const createPlayResponseLog = useCallback((payload, fallbackElapsedMs) => {
    const elapsedMs = toDisplayMs(fallbackElapsedMs);
    const diffSec = Number(((elapsedMs - TARGET_MS) / 1000).toFixed(2));
    const absDiffSec = Math.abs(diffSec);
    const isExact = payload?.playResult === 'success' || absDiffSec === 0;
    const diffText = isExact ? '정확!' : `${diffSec > 0 ? '+' : ''}${diffSec.toFixed(2)}`;
    const tone = isExact ? 'text-[#03C75A]' : absDiffSec <= 0.1 ? 'text-[#FFBC2B]' : 'text-[#F6475F]';

    return {
      id: `server-stop-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      createdAt: Date.now(),
      elapsedMs,
      timeText: formatSecondsText(elapsedMs),
      diffSec,
      absDiffSec,
      diffText,
      tone,
    };
  }, []);

  const getResultTypeFromPlayResult = useCallback((playResult) => {
    if (playResult === 'success') return 'success';
    if (playResult === 'comfort' || playResult === 'comfortBonus') return 'near';
    if (playResult === 'timeout') return 'timeout';
    return 'fail';
  }, []);

  const stopTimer = useCallback(
    (nextElapsedMs) => {
      clearRaf();
      stopBeep();
      const displayMs = toStopDisplayMs(nextElapsedMs);
      elapsedBeforeStartRef.current = displayMs;
      setTimerMs(displayMs);

      const createdLog = createLog(displayMs);
      setBestLog((prevBestLog) => getBetterLog(prevBestLog, createdLog));
      setPlayLogs((prevLogs) => [createdLog, ...pruneExpiredLogs(prevLogs)].slice(0, MAX_PLAY_LOG_COUNT));

      const isSuccess = createdLog.absDiffSec === 0;
      const isTimeOver = displayMs >= TIMER_MAX_MS;
      const isNearFail = !isSuccess && Math.abs(createdLog.elapsedMs - TARGET_MS) === NEAR_FAIL_DIFF_MS;

      if (isSuccess) {
        setDailySuccessCount((prevCount) => {
          const nextCount = Math.min(SUCCESS_DAILY_LIMIT, prevCount + 1);
          if (nextCount >= SUCCESS_DAILY_LIMIT) setIsDailyLocked(true);
          onResult({ type: 'success', createdLog, clampedMs: displayMs, successCount: nextCount });
          return nextCount;
        });
      } else if (isTimeOver) {
        onResult({ type: 'timeout', createdLog, clampedMs: displayMs });
      } else if (isNearFail) {
        onResult({ type: 'near', createdLog, clampedMs: displayMs });
      } else {
        onResult({ type: 'fail', createdLog, clampedMs: displayMs });
      }

      setPlayState('stopped');
    },
    [clearRaf, createLog, getBetterLog, onResult, pruneExpiredLogs, stopBeep],
  );

  const stopTimerWithServer = useCallback(
    async (nextElapsedMs) => {
      if (!onPlayStop || isStopPending) return;

      clearRaf();
      stopBeep();
      const displayMs = toStopDisplayMs(nextElapsedMs);
      elapsedBeforeStartRef.current = displayMs;
      setTimerMs(displayMs);
      setIsStopPending(true);

      try {
        const payload = await onPlayStop(displayMs);
        if (payload === false) {
          setPlayState('stopped');
          return;
        }

        const createdLog = createPlayResponseLog(payload, displayMs);
        elapsedBeforeStartRef.current = createdLog.elapsedMs;
        setTimerMs(createdLog.elapsedMs);

        const resultType = getResultTypeFromPlayResult(payload?.playResult);
        if (resultType === 'success') {
          const successCount = Number(payload?.state?.successDailyCount ?? dailySuccessCount + 1);
          setDailySuccessCount(Math.min(SUCCESS_DAILY_LIMIT, successCount));
          if (successCount >= SUCCESS_DAILY_LIMIT) setIsDailyLocked(true);
          onResult({ type: 'success', createdLog, clampedMs: createdLog.elapsedMs, successCount });
        } else {
          onResult({ type: resultType, createdLog, clampedMs: createdLog.elapsedMs });
        }

        setPlayState('stopped');
      } finally {
        setIsStopPending(false);
      }
    },
    [
      clearRaf,
      createPlayResponseLog,
      dailySuccessCount,
      getResultTypeFromPlayResult,
      isStopPending,
      onPlayStop,
      onResult,
      stopBeep,
    ],
  );

  const runTick = useCallback(
    (now) => {
      const elapsed = elapsedBeforeStartRef.current + (now - startAtRef.current);
      const clamped = Math.min(TIMER_MAX_MS, Math.max(0, elapsed));
      const displayMs = toDisplayMs(clamped);
      lastDisplayedMsRef.current = displayMs;
      setTimerMs(displayMs);

      if (clamped >= TIMER_MAX_MS) {
        if (onPlayStop) {
          stopTimerWithServer(TIMER_MAX_MS);
        } else {
          stopTimer(TIMER_MAX_MS);
        }
        return;
      }

      rafRef.current = requestAnimationFrame(runTick);
    },
    [onPlayStop, stopTimer, stopTimerWithServer],
  );

  const startRunning = useCallback(() => {
    clearRaf();
    startBeep();
    startAtRef.current = performance.now();
    lastDisplayedMsRef.current = toDisplayMs(elapsedBeforeStartRef.current);
    setPlayState('running');
    rafRef.current = requestAnimationFrame(runTick);
  }, [clearRaf, runTick, startBeep]);

  const resetToIdle = useCallback(() => {
    clearRaf();
    stopBeep();
    elapsedBeforeStartRef.current = 0;
    lastDisplayedMsRef.current = 0;
    setTimerMs(0);
    setPlayState('idle');
  }, [clearRaf, stopBeep]);

  const handleStartButtonClick = useCallback(async () => {
    if (playState === 'idle') {
      const startContext = getStartContext();
      if (isBlockedByPopup || isDailyLocked || isStartPending || !startContext?.canStart) return;
      setIsStartPending(true);
      try {
        const startResult = await onPlayStart(startContext.mode);
        if (startResult === false) return;
        startRunning();
      } finally {
        setIsStartPending(false);
      }
      return;
    }

    if (playState === 'running') {
      const stopMs = toStopDisplayMs(lastDisplayedMsRef.current);
      if (onPlayStop) {
        stopTimerWithServer(stopMs);
      } else {
        stopTimer(stopMs);
      }
      return;
    }

    resetToIdle();
  }, [
    getStartContext,
    isBlockedByPopup,
    isDailyLocked,
    isStartPending,
    onPlayStart,
    onPlayStop,
    playState,
    resetToIdle,
    startRunning,
    stopTimer,
    stopTimerWithServer,
  ]);

  useEffect(
    () => () => {
      clearRaf();
      stopBeep();
    },
    [clearRaf, stopBeep],
  );

  useEffect(() => {
    if (onPlayStop) return undefined;
    const prune = () => {
      setPlayLogs((prevLogs) => pruneExpiredLogs(prevLogs));
    };
    prune();
    const intervalId = setInterval(prune, 5000);
    return () => clearInterval(intervalId);
  }, [onPlayStop, pruneExpiredLogs]);

  useEffect(() => {
    if (playLogs.length <= DEFAULT_VISIBLE_LOG_COUNT && isLogsExpanded) {
      setIsLogsExpanded(false);
    }
  }, [isLogsExpanded, playLogs.length]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const nextTodayKstKey = getTodayKstKey();
      if (nextTodayKstKey !== todayKstKey) {
        setTodayKstKey(nextTodayKstKey);
        setDailySuccessCount(0);
        setIsDailyLocked(false);
      }
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [todayKstKey]);

  useEffect(() => {
    const serverState = initState?.state;
    if (!serverState) return;

    const successCount = Number(serverState.successDailyCount ?? 0);
    setDailySuccessCount(successCount);
    setIsDailyLocked(successCount >= SUCCESS_DAILY_LIMIT);

    if (Number.isFinite(Number(serverState.bestTimeSec))) {
      const bestElapsedMs = Math.round(Number(serverState.bestTimeSec) * 1000);
      const bestAbsDiffSec = Math.abs(Number(serverState.bestDiffSec ?? 0));
      setBestLog({
        id: 'server-best-log',
        createdAt: Date.now(),
        elapsedMs: bestElapsedMs,
        timeText: formatSecondsText(bestElapsedMs),
        diffSec: bestAbsDiffSec === 0 ? 0 : bestAbsDiffSec,
        absDiffSec: bestAbsDiffSec,
        diffText: bestAbsDiffSec === 0 ? '정확!' : `+${bestAbsDiffSec.toFixed(2)}`,
        tone: bestAbsDiffSec === 0 ? 'text-[#03C75A]' : bestAbsDiffSec <= 0.1 ? 'text-[#FFBC2B]' : 'text-[#F6475F]',
      });
    }

    if (Array.isArray(initState.history)) {
      const serverLogs = initState.history.map(createServerLog).slice(0, MAX_PLAY_LOG_COUNT);
      setPlayLogs(serverLogs);
    }
  }, [createServerLog, initState]);

  const startButtonLabel =
    playState === 'running' ? 'STOP' : playState === 'stopped' ? 'RESTART' : isDailyLocked ? '내일 다시!' : 'START';
  const startButtonBg =
    playState === 'running' ? '#F6475F' : playState === 'stopped' ? '#3B5CD2' : isDailyLocked ? '#8E8E8E' : '#000000';
  const timerDiffMs = Math.abs(timerMs - TARGET_MS);
  const proximityLevel =
    playState !== 'running' || timerMs < 3000 || timerMs > 3400
      ? 'none'
      : timerDiffMs <= 50
        ? 'high'
        : timerDiffMs <= 100
          ? 'mid'
          : 'low';

  return {
    playState,
    timerText: formatTimer(timerMs),
    proximityLevel,
    playLogs,
    isLogsExpanded,
    setIsLogsExpanded,
    bestLog,
    dailySuccessCount,
    isDailyLocked,
    isStartPending,
    isStopPending,
    todayKstKey,
    startButtonLabel,
    startButtonBg,
    isStartButtonDisabled: playState === 'idle' && isDailyLocked,
    handleStartButtonClick,
    resetToIdle,
    shouldShowPlayLogToggle: playLogs.length > DEFAULT_VISIBLE_LOG_COUNT,
    visiblePlayLogs: isLogsExpanded ? playLogs : playLogs.slice(0, DEFAULT_VISIBLE_LOG_COUNT),
  };
}
